import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatCurrency, formatDate } from '@/utils/formatters';
import {
  CreditCard, Eye, ChevronUp, ChevronDown, ArrowUp, ArrowDown, ArrowUpDown,
} from 'lucide-react';

export const TransactionsTab = ({ transactions, canEditTransactions, llcId }) => {
  const [txnSortColumn, setTxnSortColumn] = useState('date');
  const [txnSortDirection, setTxnSortDirection] = useState('desc');
  const [expandedMobileTxn, setExpandedMobileTxn] = useState(null);

  const handleTxnSort = (column) => {
    if (txnSortColumn === column) {
      setTxnSortDirection(txnSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setTxnSortColumn(column);
      setTxnSortDirection('desc');
    }
  };

  const sortedTransactions = useMemo(() => {
    if (!transactions.length) return [];
    return [...transactions].sort((a, b) => {
      let aVal, bVal;
      switch (txnSortColumn) {
        case 'date': aVal = new Date(a.date); bVal = new Date(b.date); break;
        case 'description': aVal = (a.description || '').toLowerCase(); bVal = (b.description || '').toLowerCase(); break;
        case 'category': aVal = (a.category_name || '').toLowerCase(); bVal = (b.category_name || '').toLowerCase(); break;
        case 'amount': aVal = a.amount || 0; bVal = b.amount || 0; break;
        default: return 0;
      }
      if (aVal < bVal) return txnSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return txnSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [transactions, txnSortColumn, txnSortDirection]);

  const SortHeader = ({ column, label, align }) => (
    <th className={`${align === 'right' ? 'text-right' : 'text-left'} px-4 py-3`}>
      <button
        onClick={() => handleTxnSort(column)}
        className={`flex items-center gap-1 text-white font-semibold hover:text-amber-300 transition-colors ${align === 'right' ? 'ml-auto' : ''}`}
      >
        {label}
        {txnSortColumn === column ? (
          txnSortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4 text-amber-400" />
        ) : (
          <ArrowUpDown className="w-4 h-4 text-slate-400" />
        )}
      </button>
    </th>
  );

  return (
    <TabsContent value="transactions" className="mt-4">
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-serif">Transaction Register</CardTitle>
            <CardDescription>
              {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
              {!canEditTransactions && ' (View Only)'}
            </CardDescription>
          </div>
          {canEditTransactions ? (
            <Link to={`/checkbook/${llcId}`}>
              <Button data-testid="go-to-checkbook-btn">
                <CreditCard className="w-4 h-4 mr-2" />
                Open Checkbook
              </Button>
            </Link>
          ) : (
            <Badge variant="outline" className="text-slate-500">
              <Eye className="w-3 h-3 mr-1" />
              Read Only
            </Badge>
          )}
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No transactions yet</p>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden">
                <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-t-lg px-4 py-3">
                  <div className="grid grid-cols-12 gap-2 text-white text-xs font-semibold">
                    <div className="col-span-4">Date</div>
                    <div className="col-span-5">Description</div>
                    <div className="col-span-3 text-right">Amount</div>
                  </div>
                </div>
                <div className="border border-t-0 border-slate-200 rounded-b-lg divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                  {sortedTransactions.map((txn) => {
                    const isExpanded = expandedMobileTxn === txn.id;
                    const isIncome = txn.category_type === 'income';
                    const isExpense = txn.category_type === 'expense';
                    return (
                      <div key={txn.id}>
                        <div
                          className="px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() => setExpandedMobileTxn(isExpanded ? null : txn.id)}
                          data-testid={`mobile-txn-row-${txn.id}`}
                        >
                          <div className="grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-4 font-mono text-sm text-slate-700">{formatDate(txn.date)}</div>
                            <div className="col-span-5 text-sm text-slate-800 truncate">{txn.description}</div>
                            <div className="col-span-3 flex items-center justify-end gap-1">
                              <span className={`font-mono text-sm font-medium ${isIncome ? 'text-emerald-600' : isExpense ? 'text-rose-600' : 'text-slate-700'}`}>
                                {isIncome ? '+' : isExpense ? '-' : ''}{formatCurrency(txn.amount)}
                              </span>
                              {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                            </div>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="px-4 pb-3 pt-1 bg-slate-50/50 border-t border-slate-100">
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Category</span>
                                <Badge variant="outline" className="capitalize text-xs bg-white">{txn.category_name}</Badge>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Type</span>
                                <span className={`capitalize font-medium ${isIncome ? 'text-emerald-600' : isExpense ? 'text-rose-600' : 'text-slate-600'}`}>
                                  {txn.category_type || 'N/A'}
                                </span>
                              </div>
                              {txn.reference && (
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Reference</span>
                                  <span className="font-mono text-slate-700">{txn.reference}</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-slate-500">Full Description</span>
                                <span className="text-slate-800 text-right max-w-[200px]">{txn.description}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block border border-slate-200 rounded-lg overflow-hidden">
                <div className="max-h-[400px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-slate-800 to-slate-700 sticky top-0 z-10">
                      <tr>
                        <SortHeader column="date" label="Date" />
                        <SortHeader column="description" label="Description" />
                        <SortHeader column="category" label="Category" />
                        <SortHeader column="amount" label="Amount" align="right" />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTransactions.map((txn, idx) => (
                        <tr key={txn.id} className={`hover:bg-slate-50 border-b border-slate-100 last:border-b-0 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                          <td className="font-mono text-sm py-2.5 px-4 text-slate-700">{formatDate(txn.date)}</td>
                          <td className="py-2.5 px-4 text-slate-800">{txn.description}</td>
                          <td className="py-2.5 px-4">
                            <Badge variant="outline" className="capitalize text-xs bg-slate-100/50">{txn.category_name}</Badge>
                          </td>
                          <td className={`text-right font-mono py-2.5 px-4 font-medium ${
                            txn.category_type === 'income' ? 'text-emerald-600' : txn.category_type === 'expense' ? 'text-rose-600' : 'text-slate-700'
                          }`}>
                            {txn.category_type === 'income' ? '+' : txn.category_type === 'expense' ? '-' : ''}{formatCurrency(txn.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
};
